import { Location } from '@angular/common';
import { EventEmitter, Injectable } from '@angular/core';
import { CompareCartService } from '@cart/data-access';
import { BaseStoreFacade, BusinessRegionFacade, CityPersistenceState } from '@city/data-access';
import { CITY_SLUG_COOKIE_KEY } from '@core/const';
import { CookieService } from '@core/feature';
import { ConfigInitializerService, RoutingService } from '@spartacus/core';
import { Observable, combineLatest } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, take } from 'rxjs/operators';
import { UserGeoPointService } from './user-geo-point.service';

@Injectable({
  providedIn: 'root',
})
export class CityService {
  readonly cityPathRe = new RegExp('\\/(\\w+)[\\/?]?');
  baseStores$ = this.baseStoreFacade.getAll();
  activeBaseStore$ = this.baseStoreFacade.getActiveBaseStore();
  activeBaseStoreCode$ = this.baseStoreFacade.getActiveCode();
  businessRegion$ = this.businessRegionFacade.getBusinessRegion();
  noDeliveryIsShowing$ = this.businessRegionFacade.getNoDeliveryIsShowing();
  currentCity$ = combineLatest({ baseStore: this.activeBaseStore$, businessRegion: this.businessRegion$ }).pipe(
    map(({ baseStore, businessRegion }) => {
      if (!baseStore || !baseStore.code) {
        return undefined;
      }
      const { name, code } = baseStore;

      if (businessRegion) {
        return {
          name: businessRegion.city?.name ?? businessRegion.point.name,
          baseStoreId: businessRegion.point.slug,
          inDeliveryZone: businessRegion.inDeliveryZone,
        };
      }

      return { name, baseStoreId: code, inDeliveryZone: true };
    }),
  );

  // события
  openCityListEvent = new EventEmitter();
  openCityDefinedDialogEvent = new EventEmitter();
  openCityNoDeliveryDialogEvent = new EventEmitter();

  constructor(
    private businessRegionFacade: BusinessRegionFacade,
    private baseStoreFacade: BaseStoreFacade,
    private cookieService: CookieService,
    private userGeoPointService: UserGeoPointService,
    private compareCartService: CompareCartService,
    private location: Location,
    private configInitializerService: ConfigInitializerService,
    private routingService: RoutingService,
  ) {}

  initOnServer(): void {
    combineLatest({ baseStoreFromUrl: this.getBaseStoreFromUrl(), baseStoreFromCookie: this.getBaseStoreFromCookie() })
      .pipe(take(1))
      .subscribe(({ baseStoreFromUrl, baseStoreFromCookie }) => {
        if (baseStoreFromUrl) {
          this.baseStoreFacade.setActive(baseStoreFromUrl);
        } else if (baseStoreFromCookie) {
          this.baseStoreFacade.setActive(baseStoreFromCookie);
        } else {
          this.userGeoPointService.obtainGeoPointServer();
        }
      });
  }

  initOnBrowser(): void {
    combineLatest({
      baseStoreFromUrl: this.getBaseStoreFromUrl(),
      baseStoreFromCookie: this.getBaseStoreFromCookie(),
      routerState: this.routingService.getRouterState(),
      activeBaseStoreCode: this.activeBaseStoreCode$,
    })
      .pipe(take(1))
      .subscribe(({ baseStoreFromUrl, baseStoreFromCookie, routerState, activeBaseStoreCode }) => {
        const baseStore = baseStoreFromUrl || baseStoreFromCookie;

        // Мы уже инитились на Бэке, если фронт с этим согласен, то нет смысла что то менять.
        if (baseStore && baseStore !== activeBaseStoreCode) {
          // после клонирования страницы вынуждены откладывать определение города, иначе бэк предыдующей корзине сменит город
          if (routerState.state.semanticRoute === 'cart') {
            this.baseStoreFacade.delayedSetActiveCode(baseStore);
          } else {
            this.baseStoreFacade.setActive(baseStore);
          }
        }
        // пробуем подергать геолокацию. Если и там нет, то будет дефолтная москва из инита
        if (!baseStore) {
          this.userGeoPointService.obtainGeoPointBrowser();
        }

        this.activeBaseStoreCode$.pipe(debounceTime(2000), take(1)).subscribe((activeBsCode) => {
          if (!baseStoreFromCookie || baseStoreFromCookie !== activeBsCode) {
            this.openCityDefinedDialog();
          }
        });

        combineLatest({
          city: this.currentCity$.pipe(distinctUntilChanged()),
          noDeliveryIsShowing: this.noDeliveryIsShowing$,
        })
          .pipe(filter(({ city, noDeliveryIsShowing }) => !!city && !city.inDeliveryZone && noDeliveryIsShowing))
          .subscribe(() => {
            this.openCityNoDeliveryDialog();
          });
      });
  }

  onReadCityState(state?: CityPersistenceState): void {
    if (state?.businessRegion) {
      this.businessRegionFacade.setBusinessRegion(state.businessRegion);
    }
  }

  setActiveBaseStoreToCookie(): void {
    this.activeBaseStoreCode$.pipe(take(1)).subscribe((code: string) => {
      this.setBaseStoreToCookie(code);
    });
  }

  setBaseStoreWithoutCompareCart(slug: string): void {
    this.setBaseStoreToCookie(slug);
    this.baseStoreFacade.setActive(slug);
  }

  setBaseStore(slug: string): void {
    this.activeBaseStoreCode$.pipe(take(1)).subscribe((baseStore: string) => {
      if (baseStore !== slug) {
        this.compareCartService.compareCart(slug);
      }

      this.businessRegionFacade.resetBusinessRegion();
    });
  }

  isBaseStore(code: string): Observable<boolean> {
    return this.configInitializerService.getStable('context.baseStore').pipe(
      map((config) => config.context.baseStore.some((i) => i === code)),
      take(1),
    );
  }

  openCityList(): void {
    this.openCityListEvent.emit();
  }

  openCityNoDeliveryDialog(): void {
    this.openCityNoDeliveryDialogEvent.emit();
  }

  openCityDefinedDialog(): void {
    this.openCityDefinedDialogEvent.emit();
  }

  setShownNoDeliveryDialog(): void {
    this.businessRegionFacade.setShownNoDelivery();
  }

  getCitySlugFormPath(pathStr: string): string {
    const match = this.cityPathRe.exec(pathStr);
    if (match && match?.length > 0) {
      return match[1];
    }

    return '';
  }

  private getBaseStoreFromUrl(): Observable<string | undefined> {
    const probableCode = this.getCitySlugFormPath(this.location.path());

    return this.isBaseStore(probableCode).pipe(map((isBaseStore) => (isBaseStore ? probableCode : undefined)));
  }

  private getBaseStoreFromCookie(): Observable<string | undefined> {
    const code = this.cookieService.get(CITY_SLUG_COOKIE_KEY);

    return this.isBaseStore(code).pipe(map((isBaseStore) => (isBaseStore ? code : undefined)));
  }

  private setBaseStoreToCookie(slug: string): void {
    const date = new Date();
    date.setMonth(date.getMonth() + 1);
    this.cookieService.put(CITY_SLUG_COOKIE_KEY, slug, {
      path: '/',
      expires: date,
    });
  }
}
